import Box from '@material-ui/core/Box';
import Card from '@material-ui/core/Card';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import React, { useMemo, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Divider from '@material-ui/core/Divider';
import { useDispatch, useSelector } from 'react-redux';
import { get } from 'lodash';
import CardContent from '@material-ui/core/CardContent';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Button, CardActions } from '@material-ui/core';
import { PermMedia } from '@material-ui/icons';
import {
  getColorByStatus,
  getDateDifferenceFromNow,
  UTCDateTimeToLocalDateTimeReadable
} from '../../../helpers';
import WorkflowHandler from '../../../components/Workflows/WorkflowHandler';
import {
  COMPLETE_PATIENT_PREFIX,
  completePatientAction,
  updateTechWorkflowProcessesAction
} from '../../../reducers/patients';
import {
  FILE_TYPE_IDS,
  PATIENT_STATUSES_VALUE,
  PRINT_REQUEST_TABLE_CONFIG,
  RX_FORM_STATUSES,
  RX_FORM_STATUSES_ID
} from '../../../constants';
import StatusTag from '../../../components/StatusTag';
import hasPermission from '../../../selectors/hasPermission';
import InitialSetupAssignment from './InitialSetupAssignmentChanger';
import { formatProcesses, formatProcessesRequest } from '../../../helpers/techWorkflow';
import techWorkflowTypes from '../../../constants/techWorkflowTypes';
import createLoadingSelector from '../../../selectors/loading';
import Can from '../../../components/Can';
import LoadingButton from '../../../components/LoadingButton';
import InitialCaseCompletionDialog from '../../../components/Shipment/Dialogs/InitialCaseCompletionDialog';
import UploadFilesDialog from '../../../components/UploadFilesDialog';

const useStyles = makeStyles(theme => ({
  requestHeader: {
    display: 'flex',
    padding: '8px 16px',
    alignItems: 'center'
  },
  initialSetupHeaderLeft: {
    display: 'flex',
    alignItems: 'center'
  },
  initialSetupHeaderRight: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center'
  },
  initialSetupContent: {
    marginTop: theme.spacing(2)
  },
  actions: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-around',
    alignItems: 'flex-end',
    height: 150
  },
  actionsFooter: {
    display: 'flex',
    justifyContent: 'flex-end'
  },
  centeredColumn: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-around',
    height: 150
  }
}));

const InitialSetupWorkflow = ({ patient, rxForm, disable, onComplete }) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const isCompleting = useSelector(state =>
    createLoadingSelector([COMPLETE_PATIENT_PREFIX])(state)
  );

  const [openFormDialog, setOpenFormDialog] = useState(false);
  const [isPhotosUploaderOpen, setIsPhotosUploaderOpen] = useState(false);

  const techWorkflow = get(rxForm, 'tech_workflow_initial_case_setup');
  const isInactive =
    [RX_FORM_STATUSES_ID.completed, RX_FORM_STATUSES_ID.canceled].indexOf(rxForm.status_id) !==
      -1 || disable;

  const initialWorkflow = useMemo(() => {
    return formatProcesses(get(techWorkflow, 'processes', []));
  }, [techWorkflow]);

  const canUpdateRxFormManagementData = useSelector(state =>
    hasPermission(state, {
      permissions: ['rx-forms.manage', 'rx-forms.update-management-data']
    })
  );

  const updateTechWorkflow = data => {
    for (const item of data) {
      let { started_at, finished_at } = item;
      started_at = moment(started_at);
      finished_at = moment(finished_at);

      if (started_at.isValid() && finished_at.isValid() && started_at.isAfter(finished_at)) {
        return Promise.reject({
          message: `Process ${item.process} - ST must be before ET`
        });
      }
    }

    return dispatch(
      updateTechWorkflowProcessesAction(
        rxForm.patient_id,
        techWorkflow.tech_workflow_id,
        techWorkflowTypes.INITIAL_CASE_SETUP,
        formatProcessesRequest(data)
      )
    );
  };

  const handlePhotosUploadDialogClose = () => {
    setIsPhotosUploaderOpen(false);
  };

  const renderUploadPhotosButton = () => (
    <Button
      className={classes.actionButton}
      variant="contained"
      size="small"
      color="default"
      startIcon={<PermMedia />}
      onClick={() => setIsPhotosUploaderOpen(true)}
    >
      UPLOAD PHOTOS
    </Button>
  );

  const completeRequest = (params = {}) => {
    dispatch(completePatientAction(rxForm.patient_id, params)).finally(() => onComplete());
    setOpenFormDialog(false);
  };

  const handleComplete = () => {
    setOpenFormDialog(true);
  };

  return (
    <Grid container>
      <Box mt={2} width="100%">
        <Card>
          <Grid item xs={12} className={classes.requestHeader}>
            <Grid container spacing={3}>
              <Grid item xs={9} className={classes.initialSetupHeaderLeft}>
                <Box mr={2}>
                  <StatusTag
                    label={RX_FORM_STATUSES[rxForm.status_id]}
                    color={getColorByStatus(RX_FORM_STATUSES[rxForm.status_id])}
                  />
                </Box>
                <Box>
                  <Typography component="h1" variant="subtitle1">
                    {`Submitted at ${UTCDateTimeToLocalDateTimeReadable(rxForm.created_at)}`}
                  </Typography>
                </Box>
                {false && (
                  <Box mx={2}>
                    <Typography component="h1" variant="subtitle2">
                      <InitialSetupAssignment rxForm={rxForm} />
                    </Typography>
                  </Box>
                )}
              </Grid>
              <Grid item xs={3} className={classes.initialSetupHeaderRight}>
                <Typography component="h1" variant="subtitle1">
                  {rxForm.status.toLowerCase() === 'pending' &&
                    `${getDateDifferenceFromNow(rxForm.status_updated_at)} ago`}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Divider />
          <CardContent>
            <Grid item xs={12} className={classes.initialSetupContent}>
              <Grid container spacing={3}>
                <Grid item xs={9}>
                  {canUpdateRxFormManagementData && (
                    <WorkflowHandler
                      update={updateTechWorkflow}
                      field="tech_workflow"
                      title="Scans Impressions Received"
                      workflowData={initialWorkflow}
                      columnsData={PRINT_REQUEST_TABLE_CONFIG}
                      readOnly={isInactive}
                    />
                  )}
                </Grid>

                {!isInactive && (
                  <Grid item xs={2} className={classes.actions}>
                    {renderUploadPhotosButton()}
                  </Grid>
                )}
              </Grid>
            </Grid>
          </CardContent>
          <Divider />
          <CardActions className={classes.actionsFooter}>
            <Can
              permissions={['patients.manage', 'patients.complete-setup', 'patients.supervise']}
              yes={() => (
                <>
                  {patient.patient_status_id === PATIENT_STATUSES_VALUE.ACTIVE && (
                    <LoadingButton
                      color="primary"
                      variant="contained"
                      size="small"
                      disabled={isCompleting}
                      className={classes.actionButton}
                      onClick={handleComplete}
                      loading={isCompleting}
                    >
                      Complete
                    </LoadingButton>
                  )}
                </>
              )}
            />
          </CardActions>
        </Card>

        <InitialCaseCompletionDialog
          setOpen={setOpenFormDialog}
          open={openFormDialog}
          onContinue={completeRequest}
          fullWidth
        />

        <UploadFilesDialog
          title="Photos"
          patientFileTypeId={FILE_TYPE_IDS.photos}
          patientId={rxForm.patient_id}
          handleClose={handlePhotosUploadDialogClose}
          open={isPhotosUploaderOpen}
          isImageUploader
          updatePatient
        />
      </Box>
    </Grid>
  );
};

InitialSetupWorkflow.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  rxForm: PropTypes.object,
  disable: PropTypes.bool,
  showWorkflowTable: PropTypes.bool,
  patient: PropTypes.object,
  onComplete: PropTypes.func
};

InitialSetupWorkflow.defaultProps = {
  rxForm: { params: {} },
  disable: false,
  showWorkflowTable: true,
  onComplete: () => {}
};

export default InitialSetupWorkflow;
